/**
 * Junior Forged / Tour Forged — shared fitting engine
 * Source of truth: client Shopify.docx + MOI continuum message
 */
(function (global) {
  'use strict';

  var MOI_BANDS = [
    { ironMax: 50, driverMid: 64, moiMin: 2400, moiMax: 2450, category: 'Young / developing' },
    { ironMax: 55, driverMid: 70.5, moiMin: 2450, moiMax: 2500, category: 'Junior' },
    { ironMax: 60, driverMid: 75.5, moiMin: 2500, moiMax: 2550, category: 'Competitive junior' },
    { ironMax: 65, driverMid: 81, moiMin: 2550, moiMax: 2600, category: 'Strong competitive' },
    { ironMax: 70, driverMid: 87, moiMin: 2600, moiMax: 2650, category: 'Advanced / elite' },
    { ironMax: 75, driverMid: 93, moiMin: 2650, moiMax: 2709, category: 'Elite junior' },
    { ironMax: 80, driverMid: 99, moiMin: 2709, moiMax: 2750, category: 'College-track' },
    { ironMax: 85, driverMid: 105, moiMin: 2750, moiMax: 2800, category: 'College / strong adult' },
    { ironMax: 999, driverMid: 112, moiMin: 2800, moiMax: 2850, category: 'High-speed' }
  ];

  var SERIES = {
    series1: {
      key: 'series1',
      label: 'Junior Forged Series 1',
      blurb: 'Lightweight performance and easy launch for developing junior swings.',
      ironHandle: 'junior-forged-series-1-irons',
      wedgeHandle: 'junior-forged-series-1-wedge',
      putterHandle: 'junior-forged-putter',
      ratings: { Distance: 5, Forgiveness: 5, Workability: 5, 'Distance control': 5 }
    },
    series2: {
      key: 'series2',
      label: 'Junior Forged Series 2',
      blurb: 'Progressive head weights and stronger lofts for advancing juniors.',
      ironHandle: 'junior-forged-series-2-irons',
      wedgeHandle: 'junior-forged-series-2-wedge',
      putterHandle: 'junior-forged-putter',
      ratings: { Distance: 5, Forgiveness: 5, Workability: 5, 'Distance control': 5 }
    },
    tour: {
      key: 'tour',
      label: 'Tour Forged',
      blurb: 'Maximum stability and control for elite juniors, collegiate, and adult players.',
      ironHandle: 'tour-forged-irons',
      wedgeHandle: 'tour-forged-wedge',
      putterHandle: 'tour-forged-putter',
      ratings: { Distance: 5, Forgiveness: 5, Workability: 5, 'Distance control': 5 }
    }
  };

  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }

  function normalizeTempo(tempo) {
    var t = String(tempo || 'moderate').toLowerCase();
    if (t === 'smooth' || t === 'slow') return 'smooth';
    if (t === 'fast' || t === 'aggressive') return 'aggressive';
    if (t === 'normal' || t === 'moderate' || t === 'neutral') return 'moderate';
    return 'moderate';
  }

  function tempoLabel(tempo) {
    var t = normalizeTempo(tempo);
    if (t === 'smooth') return 'Smooth';
    if (t === 'aggressive') return 'Fast';
    return 'Normal';
  }

  function normalizeTransition(transition) {
    var t = String(transition || '').toLowerCase();
    if (t === 'smooth' || t === 'slow') return 'Smooth';
    if (t === 'fast' || t === 'aggressive') return 'Fast';
    if (t === 'normal' || t === 'moderate') return 'Normal';
    return 'Not provided';
  }

  function normalizeSkill(skill) {
    var s = String(skill || 'advanced').toLowerCase();
    if (s === 'beginner') return 'Beginner';
    if (s === 'competitive' || s === 'elite') return 'Competitive';
    if (s === 'advanced' || s === 'intermediate') return 'Advanced';
    return 'Advanced';
  }

  /** Estimate 6-iron mph from carry (yards) when speed unknown */
  function estimateIronSpeedFromDistance(yards) {
    var y = Number(yards);
    if (!y || y <= 0) return null;
    // Rough junior/adult mid-iron carry → speed estimate
    return clamp(Math.round(y * 0.42 + 8), 40, 95);
  }

  function resolveIronSpeed(input) {
    var mph = Number(input.ironSpeed);
    if ((!mph || mph <= 0) && input.ironDist) {
      mph = estimateIronSpeedFromDistance(input.ironDist);
    }
    return clamp(mph || 67, 40, 100);
  }

  function resolveDriverSpeed(input, ironMph) {
    var mph = Number(input.driverSpeed);
    if (!mph || mph <= 0) {
      // Approx driver from 6-iron when missing
      mph = Math.round(ironMph * 1.35 + 4);
    }
    return clamp(mph, 55, 130);
  }

  function moiBandIndexForIron(ironMph) {
    for (var i = 0; i < MOI_BANDS.length - 1; i++) {
      if (ironMph < MOI_BANDS[i].ironMax) return i;
    }
    return MOI_BANDS.length - 1;
  }

  function moiBandForIron(ironMph) {
    return MOI_BANDS[moiBandIndexForIron(ironMph)];
  }

  /**
   * Continuous MOI from client grid.
   * Position within band from iron + driver mph, then tempo ±25.
   */
  function computeMoi(ironMph, driverMph, tempo) {
    var idx = moiBandIndexForIron(ironMph);
    var band = MOI_BANDS[idx];
    var prevMax = idx === 0 ? 40 : MOI_BANDS[idx - 1].ironMax;
    var ironHi = band.ironMax === 999 ? 95 : band.ironMax;
    var ironSpan = Math.max(1, ironHi - prevMax);
    var ironPos = clamp((ironMph - prevMax) / ironSpan, 0, 1);

    var driverLo = band.driverMid - 4;
    var driverHi = band.driverMid + 4;
    var driverPos = clamp((driverMph - driverLo) / Math.max(1, driverHi - driverLo), 0, 1);

    var pos = ironPos * 0.7 + driverPos * 0.3;
    var base = band.moiMin + (band.moiMax - band.moiMin) * pos;

    var t = normalizeTempo(tempo);
    var tempoAdj = t === 'smooth' ? -25 : t === 'aggressive' ? 25 : 0;
    var moi = Math.round(base + tempoAdj);
    moi = clamp(moi, 2380, 2880);

    return {
      moi: moi,
      category: band.category,
      bandMin: band.moiMin,
      bandMax: band.moiMax,
      tempoAdj: tempoAdj
    };
  }

  /**
   * KBS shaft chart + customer-facing category.
   * Tempo picks lighter vs heavier option in dual ranges.
   */
  function pickShaft(ironMph, tempo) {
    var t = normalizeTempo(tempo);
    var model;
    var category;
    var categoryLabel;
    var advancedFlag = false;

    if (ironMph < 50) {
      category = 'speed_plus';
      categoryLabel = 'Junior Forged Speed Plus';
      model = t === 'smooth' ? 'KBS Generation 40' : 'KBS Generation 50';
    } else if (ironMph < 60) {
      category = 'speed';
      categoryLabel = 'Junior Forged Speed';
      model = 'KBS MAX 45g';
    } else if (ironMph < 68) {
      category = 'speed';
      categoryLabel = 'Junior Forged Speed';
      model = 'KBS MAX 55g';
    } else if (ironMph < 75) {
      category = 'speed';
      categoryLabel = 'Junior Forged Speed';
      model = 'KBS MAX 65g';
    } else if (ironMph < 80) {
      category = 'competition';
      categoryLabel = 'Junior Forged Competition';
      model = t === 'smooth' ? 'KBS PGI 70g' : 'KBS PGI 80g';
    } else if (ironMph < 85) {
      category = 'competition';
      categoryLabel = 'Junior Forged Competition';
      if (t === 'smooth') {
        model = 'KBS PGI 80g';
      } else {
        model = 'KBS PGI 90g';
        category = 'elite';
        categoryLabel = 'Junior Forged Elite';
        if (t === 'aggressive') advancedFlag = true;
      }
    } else {
      category = 'elite';
      categoryLabel = 'Junior Forged Elite';
      model = t === 'smooth' ? 'KBS PGI 90g' : 'KBS PGI 100g';
      if (t === 'aggressive') advancedFlag = true;
    }

    return {
      model: model,
      category: category,
      categoryLabel: categoryLabel,
      advancedFlag: advancedFlag,
      display: categoryLabel + ' · ' + model
    };
  }

  function pickFlex(ironMph) {
    if (ironMph < 55) return 'L';
    if (ironMph < 62) return 'A';
    if (ironMph < 72) return 'R';
    if (ironMph < 80) return 'S';
    return 'X';
  }

  function pickSeries(ironMph, driverMph, age) {
    if (ironMph < 60 || (age <= 12 && ironMph < 65)) return SERIES.series1;
    if (ironMph < 75 || (age <= 16 && ironMph < 78 && driverMph < 98)) return SERIES.series2;
    return SERIES.tour;
  }

  function lengthFromHeight(heightIn) {
    if (heightIn < 48) return -2.0;
    if (heightIn < 52) return -1.5;
    if (heightIn < 56) return -1.0;
    if (heightIn < 60) return -0.5;
    if (heightIn < 64) return 0;
    if (heightIn < 68) return 0.5;
    if (heightIn < 72) return 1.0;
    if (heightIn < 76) return 1.5;
    return 2.0;
  }

  function lieFromHeightWtf(heightIn, wtfIn) {
    var expected = heightIn * 0.48;
    var delta = wtfIn - expected;
    if (delta <= -3) return -2;
    if (delta <= -1.5) return -1;
    if (delta < 1.5) return 0;
    if (delta < 3) return 1;
    return 2;
  }

  function formatLength(adj) {
    if (adj === 0) return 'Standard';
    if (adj > 0) return '+' + adj.toFixed(1) + '"';
    return adj.toFixed(1) + '"';
  }

  function formatLie(adj) {
    if (adj === 0) return 'Standard';
    if (adj > 0) return adj + '° upright';
    return Math.abs(adj) + '° flat';
  }

  /**
   * Grip from wrist crease → tip of index finger (inches).
   * Client: junior / undersize / regular / midsize / oversize
   */
  function pickGrip(handInches) {
    var n = Number(handInches);
    if (!n || n <= 0) {
      return { size: 'Regular', note: 'Measure wrist crease to index tip for a locked grip size.' };
    }
    if (n < 4.75) return { size: 'Junior', note: 'Hand measure under 4.75"' };
    if (n < 5.25) return { size: 'Undersize', note: 'Hand measure ' + n.toFixed(2) + '"' };
    if (n < 5.9) return { size: 'Regular', note: 'Hand measure ' + n.toFixed(2) + '"' };
    if (n < 6.4) return { size: 'Midsize', note: 'Hand measure ' + n.toFixed(2) + '"' };
    return { size: 'Oversize', note: 'Hand measure ' + n.toFixed(2) + '"' };
  }

  function gloveSizeFromHand(handInches) {
    var n = Number(handInches);
    if (!n || n <= 0) return null;
    if (n < 4.75) return 'XS';
    if (n <= 5.25) return 'XS';
    if (n <= 5.75) return 'SM';
    if (n <= 6.25) return 'M';
    if (n <= 6.75) return 'L';
    return 'L';
  }

  function ballFlightNote(flight) {
    var f = String(flight || '').toLowerCase();
    if (f === 'low') return 'Slightly firmer tip / lower launch bias noted for fitter review.';
    if (f === 'high') return 'Slightly softer tip / higher launch bias noted for fitter review.';
    if (f === 'mid' || f === 'medium') return 'Standard / medium flight path — no extra shaft bias.';
    return 'Flight not provided — no extra shaft bias.';
  }

  function ballFlightLabel(flight) {
    var f = String(flight || '').toLowerCase();
    if (f === 'low') return 'Low';
    if (f === 'high') return 'High';
    if (f === 'mid' || f === 'medium') return 'Medium';
    return 'Not provided';
  }

  function displayOrUnknown(value, unknown) {
    if (unknown || value === null || value === undefined || value === '' || value === 'unknown') {
      return 'I don’t know';
    }
    return String(value);
  }

  function formatHeightInches(inches) {
    var n = Number(inches);
    if (!n) return '';
    var ft = Math.floor(n / 12);
    var inch = Math.round(n % 12);
    if (inch === 12) {
      ft += 1;
      inch = 0;
    }
    return ft + "'" + inch + '"';
  }

  function recommend(rawInput) {
    var input = rawInput || {};
    var height = Number(input.height) || 64;
    var wtf = Number(input.wtf) || height * 0.48;
    var age = Number(input.age) || 16;
    var tempo = normalizeTempo(input.tempo);
    var ironMph = resolveIronSpeed(input);
    var driverMph = resolveDriverSpeed(input, ironMph);
    var hand = Number(input.handMeasure) || Number(input.gripMeasure) || 0;

    var moiInfo = computeMoi(ironMph, driverMph, tempo);
    var shaft = pickShaft(ironMph, tempo);
    var flex = pickFlex(ironMph);
    var series = pickSeries(ironMph, driverMph, age);
    var lengthAdj = lengthFromHeight(height);
    var lieAdj = lieFromHeightWtf(height, wtf);
    var grip = pickGrip(hand);
    var glove = gloveSizeFromHand(hand);
    var flight = ballFlightNote(input.ballFlight);

    return {
      ironMph: ironMph,
      driverMph: driverMph,
      tempo: tempo,
      tempoLabel: tempoLabel(tempo),
      transitionLabel: normalizeTransition(input.transition),
      skillLabel: normalizeSkill(input.skill),
      series: series,
      length: formatLength(lengthAdj),
      lengthAdj: lengthAdj,
      lie: formatLie(lieAdj),
      lieAdj: lieAdj,
      flex: flex,
      shaft: shaft.display,
      shaftModel: shaft.model,
      shaftCategory: shaft.categoryLabel,
      shaftCategoryKey: shaft.category,
      advancedFittingFlag: shaft.advancedFlag,
      moi: moiInfo.moi,
      moiCategory: moiInfo.category,
      moiTempoAdj: moiInfo.tempoAdj,
      grip: grip.size,
      gripNote: grip.note,
      gloveSize: glove,
      ballFlightNote: flight,
      ballFlightLabel: ballFlightLabel(input.ballFlight),
      answers: {
        height: input.heightUnknown ? 'I don’t know' : formatHeightInches(input.height) || 'I don’t know',
        age: displayOrUnknown(input.age, input.ageUnknown),
        weight: displayOrUnknown(input.weight, input.weightUnknown),
        wtf: input.wtfUnknown ? 'I don’t know' : input.wtf ? Number(input.wtf).toFixed(Number(input.wtf) % 1 ? 1 : 0) + '"' : 'I don’t know',
        handMeasure: input.handUnknown ? 'I don’t know' : input.handMeasure ? input.handMeasure + '"' : 'I don’t know',
        ironLength: input.ironLengthUnknown ? 'I don’t know' : input.ironLength ? Number(input.ironLength).toFixed(2).replace(/\.00$/, '') + '"' : 'I don’t know',
        driverSpeed: displayOrUnknown(input.driverSpeed, input.driverSpeedUnknown),
        driverDist: displayOrUnknown(input.driverDist, input.driverDistUnknown),
        driverBallSpeed: displayOrUnknown(input.driverBallSpeed, input.driverBallSpeedUnknown),
        ironSpeed: displayOrUnknown(input.ironSpeed, input.ironSpeedUnknown),
        ironBallSpeed: displayOrUnknown(input.ironBallSpeed, input.ironBallSpeedUnknown),
        ironDist: displayOrUnknown(input.ironDist, input.ironDistUnknown),
        ironSpin: displayOrUnknown(input.ironSpin, input.ironSpinUnknown),
        playerFeedback: input.playerFeedback || '',
        swingVideo: input.swingVideoName || ''
      },
      putterShaft: 'KBS Tour Stepped Black .370',
      buildLeadTime: '10 days from order to shipment (includes video review window)',
      fittingScope: 'Tied to this order only — no account required before purchase',
      summary:
        'Recommended ' +
        series.label +
        ' with ' +
        shaft.categoryLabel +
        ' (' +
        shaft.model +
        '), ' +
        formatLength(lengthAdj) +
        ' length, ' +
        formatLie(lieAdj) +
        ' lie, flex ' +
        flex +
        ', grip ' +
        grip.size +
        ', target MOI ' +
        moiInfo.moi.toLocaleString('en-US') +
        ' kg·cm².'
    };
  }

  function fitProperties(result) {
    var answers = result.answers || {};
    var props = {
      Series: result.series.label,
      'Shaft category': result.shaftCategory,
      Shaft: result.shaftModel,
      Flex: result.flex,
      Length: result.length,
      Lie: result.lie,
      Grip: result.grip,
      'Fit MOI': String(result.moi) + ' kg-cm2',
      Tempo: result.tempoLabel,
      Transition: result.transitionLabel,
      Experience: result.skillLabel,
      'Ball flight': result.ballFlightLabel,
      'Build window': result.buildLeadTime,
      'Putter shaft': result.putterShaft
    };

    if (answers.height) props.Height = answers.height;
    if (answers.age) props.Age = answers.age;
    if (answers.weight) props.Weight = answers.weight;
    if (answers.wtf) props['Wrist-to-floor'] = answers.wtf;
    if (answers.handMeasure) props['Hand length'] = answers.handMeasure;
    if (answers.ironLength) props['Current 6i length'] = answers.ironLength;
    if (answers.driverSpeed) props['Driver swing speed'] = answers.driverSpeed;
    if (answers.driverDist) props['Driver total distance'] = answers.driverDist;
    if (answers.driverBallSpeed) props['Driver ball speed'] = answers.driverBallSpeed;
    if (answers.ironSpeed) props['6i swing speed'] = answers.ironSpeed;
    if (answers.ironBallSpeed) props['6i ball speed'] = answers.ironBallSpeed;
    if (answers.ironDist) props['6i carry'] = answers.ironDist;
    if (answers.ironSpin) props['6i spin'] = answers.ironSpin;
    if (answers.playerFeedback) {
      props.Feedback = String(answers.playerFeedback).slice(0, 250);
    }
    if (answers.swingVideo) {
      props['Swing video'] = String(answers.swingVideo).slice(0, 100);
    }

    return props;
  }

  global.GgFittingEngine = {
    recommend: recommend,
    computeMoi: computeMoi,
    pickShaft: pickShaft,
    pickGrip: pickGrip,
    gloveSizeFromHand: gloveSizeFromHand,
    fitProperties: fitProperties,
    estimateIronSpeedFromDistance: estimateIronSpeedFromDistance,
    SERIES: SERIES,
    normalizeTempo: normalizeTempo,
    tempoLabel: tempoLabel,
    normalizeTransition: normalizeTransition,
    normalizeSkill: normalizeSkill
  };
})(typeof window !== 'undefined' ? window : this);
